import React from 'react';
import i18n from 'util/i18n';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import { LegalPoliceSentenceModel } from 'forms/sections/legal/police/shared/legalPoliceSentence';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import Branch from 'components/Form/Branch';
import NotApplicable from 'components/Form/NotApplicable';
import DateRange from 'components/Form/DateRange';
import PVQLocation from 'components/Form/PVQLocation';
import { isPVQForm } from 'helpers/form';

interface Props extends Partial<LegalPoliceSentenceModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  attribute?: string
  requireLegalOffenseSentenced?: boolean
  requireLegalOffenseIncarcerated?: boolean
}

export default class Sentence extends ValidationElement<Props> {
  update = (queue: Partial<LegalPoliceSentenceModel>) => {
    this.props.onUpdate({
      Description: this.props.Description,
      ExceedsYear: this.props.ExceedsYear,
      Incarcerated: this.props.Incarcerated,
      IncarcerationDates: this.props.IncarcerationDates,
      IncarcerationDatesNA: this.props.IncarcerationDatesNA,
      IncarcerationFacility: this.props.IncarcerationFacility,
      IncarcerationFacilityAddress: this.props.IncarcerationFacilityAddress,
      GrantedParole: this.props.GrantedParole,
      GrantedParoleDates: this.props.GrantedParoleDates,
      ProbationDates: this.props.ProbationDates,
      ProbationDatesNA: this.props.ProbationDatesNA,
      ...queue,
    });
  };

  updateDescription = (values: LegalPoliceSentenceModel['Description']) => {
    this.update({
      Description: values,
    });
  };

  updateExceedsYear = (values: LegalPoliceSentenceModel['ExceedsYear']) => {
    this.update({
      ExceedsYear: values,
    });
  };

  updateIncarcerated = (values: LegalPoliceSentenceModel['Incarcerated']) => {
    this.update({
      Incarcerated: values,
      IncarcerationDatesNA: { applicable: true },
    });
  };

  updateIncarcerationDates = (values: LegalPoliceSentenceModel['IncarcerationDates']) => {
    this.update({
      IncarcerationDates: values,
      IncarcerationDatesNA: { applicable: true },
    });
  };

  updateIncarcerationFacility = (values: LegalPoliceSentenceModel['IncarcerationFacility']) => {
    this.update({
      IncarcerationFacility: values,
    });
  };

  updateIncarcerationFacilityAddress = (values: LegalPoliceSentenceModel['IncarcerationFacilityAddress']) => {
    this.update({
      IncarcerationFacilityAddress: values,
    });
  };

  updateGrantedParole = (values: LegalPoliceSentenceModel['GrantedParole']) => {
    this.update({
      GrantedParole: values,
    });
  };

  updateGrantedParoleDates = (values: LegalPoliceSentenceModel['GrantedParoleDates']) => {
    this.update({
      GrantedParoleDates: values,
    });
  };

  updateProbationDates = (values: LegalPoliceSentenceModel['ProbationDates']) => {
    this.update({
      ProbationDates: values,
    });
  };

  updateIncarcerationDatesNA = (values: LegalPoliceSentenceModel['IncarcerationDatesNA']) => {
    this.update({
      IncarcerationDates: values.applicable ? this.props.IncarcerationDates : {},
      IncarcerationDatesNA: values,
    });
  };

  updateProbationDatesNA = (values: LegalPoliceSentenceModel['ProbationDatesNA']) => {
    this.update({
      ProbationDates: values.applicable ? this.props.ProbationDates : {},
      ProbationDatesNA: values,
    });
  };

  render() {
    const {
      requireLegalOffenseSentenced, requireLegalOffenseIncarcerated, attribute, formType, sentenceJudgements
    } = this.props;
    const isPVQ = isPVQForm(formType);
    return isPVQ ? (
      <div className="sentence">

        {(sentenceJudgements.values || []).includes('Imprisonment') && (
          <>
            <Field
              // You indicated you were imprisoned. When were you imprisoned?
              title={i18n.t('legal.pvq.police.heading.incarcerationDates')}
              titleSize="h4"
              adjustFor="daterange"
              shrink
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.IncarcerationDates`}
            >
              <DateRange
                {...this.props.IncarcerationDates}
                name="IncarcerationDates"
                className="incarceration-dates"
                minDateEqualTo
                onUpdate={this.updateIncarcerationDates}
                onError={this.props.onError}
                required={this.props.required}
                allowPresent={false}
                hideFromDay
                hideToDay
                attribute={`${attribute}.IncarcerationDates`}
              />
            </Field>
            <Field
              title={i18n.t('legal.pvq.police.heading.incarcerationFacility')} // 'What is the name of the facility in which you were incarcerated
              titleSize="h4"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.IncarcerationFacility`}
              exactAttribute
            >
              <Text
                {...this.props.IncarcerationFacility}
                name="IncarcerationFacility"
                className="offense-incarceration-facility"
                onUpdate={this.updateIncarcerationFacility}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.IncarcerationFacility`}
              />
            </Field>

            <Field
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.IncarcerationFacilityAddress`}
            >
              <PVQLocation
                {...this.props.IncarcerationFacilityAddress}
                name="IncarcerationFacilityAddress"
                attribute={`${attribute}.IncarcerationFacilityAddress`}
                label={i18n.t('legal.pvq.police.heading.incarcerationFacilityAddress')}
                domesticTitle={i18n.t('legal.pvq.police.label.facilityAddressUS')}
                foreignTitle={i18n.t('legal.pvq.police.label.facilityAddressForeign')}
                layout={Location.ADDRESS}
                geocode
                addressBook="Facility"
                addressBooks={this.props.addressBooks}
                dispatch={this.props.dispatch}
                onUpdate={this.updateIncarcerationFacilityAddress}
                onError={this.props.onError}
                required={this.props.required}
                isPoBoxAllowed={false}
                showInternationalStreet={false}
              />
            </Field>
            <br />
            <Branch
              {...this.props.GrantedParole}
              name="granted_parole"
              label={i18n.t('legal.pvq.police.heading.grantedParole')} // Were you granted parole or supervised release?
              labelSize="h4"
              className="granted_parole"
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateGrantedParole}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.GrantedParole`}
            />
            {(this.props.GrantedParole?.value === 'Yes') && (
              <Field
                // You indicated you were on probation. What are or were the dates of your probation?
                title={i18n.t('legal.pvq.police.heading.grantedParoleDates')}
                titleSize="h4"
                adjustFor="daterange"
                shrink
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.GrantedParoleDates`}
              >
                <DateRange
                  {...this.props.GrantedParoleDates}
                  name="GrantedParoleDates"
                  className="granted-parole-dates"
                  minDateEqualTo
                  present={false}
                  onUpdate={this.updateGrantedParoleDates}
                  onError={this.props.onError}
                  required={this.props.required}
                  allowPresent={false}
                  hideFromDay
                  hideToDay
                  attribute={`${attribute}.GrantedParoleDates`}
                />
              </Field>
            )}
          </>
        )}
        {(sentenceJudgements.values || []).includes('Probation') && (
          <Field
            title={i18n.t('legal.pvq.police.heading.probationDates')}
            titleSize="h4"
            adjustFor="daterange"
            shrink
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ProbationDates`}
          >
            <DateRange
              {...this.props.ProbationDates}
              name="ProbationDates"
              className="probation-dates"
              minDateEqualTo
              onUpdate={this.updateProbationDates}
              onError={this.props.onError}
              required={this.props.required}
              allowPresent={false}
              hideFromDay
              hideToDay
              attribute={`${attribute}.ProbationDates`}
            />
          </Field>
        )}
      </div>
    )
      : (
        <div className="sentence">
          <Field
            title={i18n.t('legal.police.heading.sentenceDescription')}
            titleSize="h4"
            adjustFor="labels"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Description`}
          >
            <Textarea
              {...this.props.Description}
              className="description"
              name="description"
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateDescription}
              attribute={`${attribute}.Description`}
            />
          </Field>

          {requireLegalOffenseSentenced && (
            <Branch
              name="exceeding_year"
              label={i18n.t('legal.police.heading.exceedsYear')}
              labelSize="h4"
              help="legal.police.help.exceedsYear"
              className="exceeds-year"
              {...this.props.ExceedsYear}
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateExceedsYear}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ExceedsYear`}
            />
          )}

          {requireLegalOffenseIncarcerated && (
            <Branch
              name="incarcerated"
              label={i18n.t('legal.police.heading.incarcerated')}
              labelSize="h4"
              help="legal.police.help.incarcerated"
              className="incarcerated"
              {...this.props.Incarcerated}
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateIncarcerated}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Incarcerated`}
            />
          )}

          <Field
            title={i18n.t('legal.police.heading.incarcerationDates')}
            titleSize="h4"
            adjustFor="daterange"
            shrink
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.IncarcerationDates`}
          >
            {this.props.Incarcerated?.value !== 'Yes' && (
              <NotApplicable
                name="IncarcerationDatesNA"
                {...this.props.IncarcerationDatesNA}
                label={i18n.t('legal.police.label.notApplicable')}
                or={i18n.m('legal.police.para.or')}
                className="incarceration-dates-na"
                onError={this.props.onError}
                required={this.props.required}
                onUpdate={this.updateIncarcerationDatesNA}
                attribute={`${attribute}.IncarcerationDatesNA`}
                includeNAConfirmation={Boolean(this.props.IncarcerationDates?.from?.month || this.props.IncarcerationDates?.from?.year
                  || this.props.IncarcerationDates?.to?.month || this.props.IncarcerationDates?.to?.year)}
              >
                <DateRange
                  name="IncarcerationDates"
                  className="incarceration-dates"
                  {...this.props.IncarcerationDates}
                  minDateEqualTo
                  onUpdate={this.updateIncarcerationDates}
                  onError={this.props.onError}
                  required={this.props.required}
                  hideFromDay
                  hideToDay
                  attribute={`${attribute}.IncarcerationDates`}
                />
              </NotApplicable>
            )}
            {this.props.Incarcerated?.value === 'Yes' && (
              <DateRange
                name="IncarcerationDates"
                className="incarceration-dates"
                {...this.props.IncarcerationDates}
                minDateEqualTo
                onUpdate={this.updateIncarcerationDates}
                onError={this.props.onError}
                required={this.props.required}
                hideFromDay
                hideToDay
                attribute={`${attribute}.IncarcerationDates`}
              />
            )}
          </Field>

          <Field
            title={i18n.t('legal.police.heading.probationDates')}
            titleSize="h4"
            adjustFor="daterange"
            shrink
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ProbationDates`}
          >
            <NotApplicable
              name="ProbationDatesNA"
              {...this.props.ProbationDatesNA}
              label={i18n.t('legal.police.label.notApplicable')}
              or={i18n.m('legal.police.para.or')}
              className="probation-dates-na"
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateProbationDatesNA}
              attribute={`${attribute}.ProbationDatesNA`}
              includeNAConfirmation={Boolean(this.props.ProbationDates?.from?.month || this.props.ProbationDates?.from?.year
                || this.props.ProbationDates?.to?.month || this.props.ProbationDates?.to?.year)}
            >
              <DateRange
                name="ProbationDates"
                className="probation-dates"
                {...this.props.ProbationDates}
                minDateEqualTo
                onUpdate={this.updateProbationDates}
                onError={this.props.onError}
                required={this.props.required}
                hideFromDay
                hideToDay
                attribute={`${attribute}.ProbationDates`}
              />
            </NotApplicable>
          </Field>
        </div>
      );
  }
}

Sentence.defaultProps = {
  Description: {},
  ExceedsYear: {},
  Incarcerated: {},
  IncarcerationDates: {},
  ProbationDates: {},
  IncarcerationDatesNA: { applicable: true },
  ProbationDatesNA: { applicable: true },
  IncarcerationFacility: {}, // PVQ Only
  IncarcerationFacilityAddress: {}, // PVQ Only
  GrantedParole: {}, // PVQ Only
  GrantedParoleDates: {}, // PVQ Only
  sentenceJudgements: {}, // PVQ Only
  onUpdate: () => { },
  onError: (value, arr: ErrorCode) => arr,
};
